import { Cart as CartApi } from "~/api/Cart";
import { CartHelper } from "~/helpers/CartHelper";
import { Product } from "~/api/Product";
import { UserService } from "~/services/UserService";

// Source of truth switches with auth state: guests keep the existing
// localStorage cart untouched; once logged in, the server cart (shared
// across every poddomain) takes over. mergeLocalIntoServer() is the one-time
// bridge between the two, run right after a login succeeds.
//
// `count` is a shared reactive ref (useState) so the header badge updates
// on its own the moment any add-to-cart call happens anywhere on the page —
// no manual event bus needed.
// Plain module-level singleton — NOT useState. The class instance carries no
// per-request data of its own (all real state lives in the `count` useState
// below), so a shared singleton is safe; wrapping the *instance* itself in
// useState crashed SSR (Nuxt tries to serialize it into the page payload via
// devalue, which can't stringify a class instance, only plain data).
let instance: CartService | null = null;

export class CartService {

    static getInstance(): CartService {
        if (!instance) instance = new CartService();
        return instance;
    }

    get count() {
        return useState<number>("cartCount", () => 0);
    }

    private async isAuthed(): Promise<boolean> {
        if (!import.meta.client) return false;
        const user = await UserService.getInstance().getCurrent();
        return !!user;
    }

    async refresh(): Promise<void> {
        if (!import.meta.client) return;

        if (await this.isAuthed()) {
            try {
                const data = await CartApi.get();
                this.count.value = data?.totalCount ?? 0;
            } catch {
                this.count.value = 0;
            }
            return;
        }
        this.count.value = CartHelper.getCart().value.items.reduce((sum, i) => sum + i.count, 0);
    }

    async addProduct(
        productId: number,
        name: string,
        price: number,
        variantId: number | null = null,
        quantity: number = 1,
        itemId: number | null = null,
        attributeLabel: string | null = null,
        attributeValue: string | null = null,
        vibeId: number | null = null
    ): Promise<void> {
        if (await this.isAuthed()) {
            await CartApi.add({
                product_id: productId,
                item_id: itemId,
                vibe_id: vibeId,
                attribute_label: attributeLabel,
                attribute_value: attributeValue,
                name,
                price,
                quantity,
                source_domain: currentDomain(),
            });
        } else {
            CartHelper.getCart().value.addProduct(productId, variantId, quantity, itemId, attributeLabel, attributeValue, vibeId);
        }
        await this.refresh();
    }

    /** Текущий промокод корзины (сервер/локал в зависимости от авторизации). */
    async getPromoCode(): Promise<string | null> {
        if (await this.isAuthed()) {
            try {
                const data = await CartApi.get();
                return data?.partnerCode ?? null;
            } catch {
                return null;
            }
        }
        return CartHelper.getCart().value.partnerCode;
    }

    /**
     * Применить промокод. Возвращает данные партнёра при успехе, либо null
     * если код невалиден.
     */
    async applyPromoCode(code: string): Promise<{ code: string; slug: string; name: string } | null> {
        if (await this.isAuthed()) {
            try {
                const data = await CartApi.promo(code);
                return data?.partner ?? null;
            } catch {
                return null;
            }
        }
        // Гость: валидируем через публичный чек, храним локально.
        try {
            const { Partner } = await import("~/api/Partner");
            const res = await Partner.check(code);
            if (!res?.valid) return null;
            CartHelper.getCart().value.setPartnerCode(code);
            return res.partner ?? null;
        } catch {
            return null;
        }
    }

    async removePromoCode(): Promise<void> {
        if (await this.isAuthed()) {
            try { await CartApi.removePromo(); } catch { /* noop */ }
        } else {
            CartHelper.getCart().value.setPartnerCode(null);
        }
    }

    /**
     * Line-based позиции для POST /orders/make (ТЗ-017a). Каждая позиция
     * корзины уходит своей строкой со своими item_id / vibe_id.
     *
     * @param lines данные из корзины страницы (basket.vue) — уже с разрешёнными
     *   variantId/itemId/vibeId/count/price
     */
    appendOrderPositions(
        formData: FormData,
        lines: Array<{ productId: number; itemId?: number | null; vibeId?: number | null; count: number; price: number }>
    ): void {
        lines.forEach((line, i) => {
            formData.append(`positions[${i}][product_id]`, String(line.productId));
            if (line.itemId) formData.append(`positions[${i}][item_id]`, String(line.itemId));
            if (line.vibeId) formData.append(`positions[${i}][vibe_id]`, String(line.vibeId));
            formData.append(`positions[${i}][quantity]`, String(line.count));
            formData.append(`positions[${i}][price]`, String(line.price));
        });
    }

    /**
     * Folds the guest's localStorage cart into the server cart. Call once,
     * right after a login/auth-sync succeeds. Clears localStorage after —
     * the server cart is authoritative from that point on.
     */
    async mergeLocalIntoServer(): Promise<void> {
        const localCart = CartHelper.getCart().value;
        if (!localCart.items.length) {
            await this.refresh();
            return;
        }

        const ids = localCart.items.map((i) => i.variantId);
        let products: any[] = [];
        try {
            const data = await Product.getVariants(ids);
            products = data.products;
        } catch {
            return;
        }

        const items = localCart.items
            .map((item) => {
                const product = products.find((p) => p.id === item.id);
                const variant = product?.getVariant?.(item.variantId) ?? product;
                if (!product) return null;
                return {
                    product_id: item.id,
                    item_id: item.itemId,
                    vibe_id: item.vibeId,
                    attribute_label: item.attributeLabel,
                    attribute_value: item.attributeValue,
                    name: product.name,
                    price: variant?.price ?? product.price,
                    quantity: item.count,
                    source_domain: currentDomain(),
                };
            })
            .filter((i): i is NonNullable<typeof i> => i !== null);

        if (items.length) {
            try {
                await CartApi.merge(items, localCart.partnerCode);
                localCart.clear();
            } catch {
                /* keep the local cart if the merge failed — nothing lost */
            }
        }

        await this.refresh();
    }

}

function currentDomain(): string {
    if (!import.meta.client) return "";
    return window.location.hostname;
}
